/**
 * Darstellung und Pflege redaktioneller Produkttexte.
 *
 * Dieselbe Komponente traegt den Artikel-, den Modell- und den Familienfall.
 * Alle drei zeigen dieselben Angaben, folgen denselben Regeln und benutzen
 * dasselbe Bleistiftprinzip -- eine zweite Umsetzung waere eine zweite
 * Auffassung davon, was ein Produkttext ist.
 *
 * Der Unterschied zwischen ihnen ist klein und liegt ausschliesslich im Laden
 * der Daten: `ArticleTexts` loest ueber die Vererbung auf, `ModelTexts` und
 * `FamilyTexts` zeigen die Texte ihrer Ebene selbst. Alle reichen dieselbe
 * Liste von `TextSlot` hier herein.
 *
 * EINE SPRACHE, NICHT ZWEI.
 *
 * Bis zum 17.09.2026 stand unter jedem Texttyp die deutsche UND die englische
 * Fassung untereinander -- sechs Typen, zwoelf Bloecke, die Haelfte davon der
 * Satz „keine Fassung vorhanden". Wer die Akte aufschlug, las vor allem
 * Leerstellen und musste den Text, der ihn angeht, dazwischen suchen.
 *
 * Gezeigt wird deshalb genau die Fassung in der Sprache, die der
 * Sprachumschalter oben rechts nennt. Er ist bereits die Sprache der ganzen
 * Akte; eine zweite, nur fuer Texte geltende Auswahl waere ein zweiter
 * Schalter mit derselben Bedeutung -- und zwei Schalter fuer eine Frage sind
 * einer zu viel.
 *
 * Fehlt die Fassung in dieser Sprache, sagt die Ansicht das fuer DIESE
 * Sprache und zeigt NICHT ersatzweise die andere. Ein deutscher Text unter
 * einer englischen Ueberschrift sieht aus wie eine Uebersetzung, ist aber
 * keine -- und genau so kommt er in ein englisches Angebot.
 *
 * Vier Dinge macht diese Ansicht ausdruecklich sichtbar, statt sie zu
 * verschlucken:
 *   1. eine fehlende Sprachfassung        -> „keine Fassung auf Deutsch"
 *   2. WOHER der gezeigte Text stammt     -> eigener, geerbter, regelgebildeter
 *   3. ein Original, das nach seiner Uebersetzung geaendert wurde
 *   4. Platzhalter, die an dieser SKU nicht aufgeloest werden konnten
 *
 * Der Textinhalt selbst ist ein fachlicher Datenwert und wird nicht
 * uebersetzt -- er WIRD in der jeweiligen Sprache gepflegt.
 *
 * LESEN ZUERST.
 *
 * Im Lesemodus stehen der Text, seine Herkunft, sein Stand und alles, was
 * an ihm nicht stimmt. Die uebrige Buchhaltung einer Fassung -- Entstehung,
 * Supplier, Freigabevermerk -- liegt eine Zeile tiefer hinter „Details", und
 * die Knoepfe, die etwas aendern, liegen hinter dem Bleistift. Verloren geht
 * dabei nichts: Freigeben, Pruefen, Zuruecksetzen, Uebersetzen, Entwurf holen
 * und Entfernen sind allesamt im Bearbeitungsformular versammelt, also einen
 * Klick entfernt statt null.
 */
import { useState } from "react";
import type {
  ProductText, SubjectLevel, TextLanguageCode, TextLocale, TextMethod,
  TextOrigin, TextStatus, TextTypeCode,
} from "@homebase/shared";
import { TEXT_LANGUAGE_CODES, TEXT_METHODS, TEXT_ORIGINAL_METHODS, TEXT_ORIGINS } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import "./ProductTexts.css";

/** Woran der Text haengt. Genau EINES -- nie mehrere. */
export interface TextSubject {
  readonly kind: "article" | "family" | "model";
  readonly id: number;
}

/**
 * Ein Texttyp in der Darstellung.
 *
 * `editable` ist bewusst getrennt von `inherited`: ein geerbter Text ist hier
 * nicht bearbeitbar, weil er anderswo gepflegt wird -- und nicht, weil er
 * geerbt ist. Kaeme spaeter ein weiterer nicht bearbeitbarer Fall hinzu,
 * bliebe die Bedingung dieselbe.
 */
export interface TextSlot {
  readonly type: TextTypeCode;
  readonly maxLength: number | null;
  readonly text: ProductText | null;
  readonly inherited: boolean;
  /** `level` unterscheidet Produktmodell von Produktfamilie als Erbquelle. */
  readonly inheritedFrom:
    { readonly id: number; readonly name: string; readonly level: SubjectLevel } | null;
  readonly editable: boolean;
  /**
   * Darf hier ein EIGENER Text des Bezugsobjekts entstehen, obwohl gerade
   * ein geerbter angezeigt wird?
   *
   * Das ist der Kern der Trennung: ein Modelltext beschreibt die Plattform.
   * Er steht an der SKU als Kontext -- aber er ist nicht ihr Produkttext,
   * und der Weg zu einem eigenen darf nicht verstellt sein.
   */
  readonly ownAllowed?: boolean;
  /**
   * DIE ZEILE, DIE HOMEBASE AN ANDERER STELLE BEREITS ZEIGT.
   *
   * Der Produktkopf bildet die Kurzbeschreibung aus der Auswahlmatrix der
   * Produktfamilie und zeigt sie -- gespeichert wird sie nicht. Im Texte-Tab
   * stand dafuer bis zum 17.09.2026 „keine Fassung vorhanden". Beides war
   * fuer sich genommen richtig und zusammen ein Widerspruch: derselbe
   * Bildschirm behauptete, es gebe eine Kurzbeschreibung und es gebe keine.
   *
   * Aufgeloest wird er nicht dadurch, dass eine der beiden Stellen schweigt,
   * sondern dadurch, dass die zweite den Text NENNT und dazusagt, woher er
   * kommt und dass er nicht gespeichert ist. Der Wert stammt aus derselben
   * Serverantwort wie der Kopf -- hier wird nichts zweitgerechnet.
   */
  readonly ruleText?:
    { readonly body: string; readonly language: TextLanguageCode } | null;
}

export function ProductTexts({ slots, subject, mayWrite, mayRelease, supplier, editing, setEditing, onChanged, onError }: {
  slots: readonly TextSlot[];
  subject: TextSubject;
  mayWrite: boolean;
  mayRelease: boolean;
  /** Nur Artikel haben einen Supplier; bei Familien bleibt das Feld leer. */
  supplier: { id: number; name: string } | null;
  editing: string | null;
  setEditing: (key: string | null) => void;
  onChanged: () => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  // Einmal oben geholt und weitergereicht -- ein Hook gehoert nicht in eine
  // Schleife.
  const i18n = useI18n();

  /*
   * DIE TEXTSPRACHE IST DIE OBERFLAECHENSPRACHE.
   *
   * Beide Wertevorraete sind heute deckungsgleich ("de" | "en"). Liefen sie
   * auseinander -- eine dritte Oberflaechensprache ohne Textsprache -- schluege
   * genau diese Zuweisung beim Uebersetzen fehl. Das ist gewollt: der Fehler
   * gehoert in den Build und nicht in eine Akte, die dann still leer bliebe.
   */
  const lang: TextLanguageCode = i18n.lang;

  return (
    <>
      {slots.map((item) => (
        <TextTypeBlock
          key={item.type} item={item} i18n={i18n} lang={lang} subject={subject}
          mayWrite={mayWrite} mayRelease={mayRelease} supplier={supplier}
          editing={editing} setEditing={setEditing}
          onChanged={onChanged} onError={onError}
        />
      ))}
    </>
  );
}

function TextTypeBlock({
  item, i18n, lang, subject, mayWrite, mayRelease, supplier, editing, setEditing, onChanged, onError,
}: {
  item: TextSlot; i18n: I18n; lang: TextLanguageCode; subject: TextSubject;
  mayWrite: boolean; mayRelease: boolean; supplier: { id: number; name: string } | null;
  editing: string | null; setEditing: (k: string | null) => void;
  onChanged: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const typeLabel = i18n.tCode("textType", item.type, item.type);
  const langLabel = i18n.tCode("textLang", lang, lang);
  const text: ProductText | null = item.text;
  const locale = text?.locales.find((l) => l.language === lang) ?? null;
  const rule = item.ruleText ?? null;

  /* Zwei Wege in dasselbe Formular: die eigene Fassung bearbeiten -- oder,
     bei einem geerbten Text, eine EIGENE anlegen. Der zweite Weg darf die
     geerbte Fassung nicht anfassen, also bekommt er weder `locale` noch
     `text` mit. Der Schluessel traegt weiterhin die Sprache: er ueberlebt
     einen Sprachwechsel bei offenem Formular nicht, und genau das ist
     richtig -- sonst stuende deutscher Text in einem englischen Feld. */
  const key = `${item.type}:${lang}`;
  const eigenKey = `${key}:eigen`;
  const open = editing === key;
  const openEigen = editing === eigenKey;

  /*
   * Fassungen in anderen Sprachen werden NICHT gezeigt -- aber ihr Dasein
   * wird genannt. Ohne diesen Hinweis sieht ein Typ, der nur auf Deutsch
   * gepflegt ist, in der englischen Akte genauso aus wie einer, den es
   * ueberhaupt nicht gibt; das eine ist eine Uebersetzungsaufgabe, das
   * andere eine Schreibaufgabe.
   */
  const andere = TEXT_LANGUAGE_CODES.filter(
    (l) => l !== lang && (text?.locales.some((x) => x.language === l) ?? false));

  /*
   * Wer die regelgebildete Zeile liest und „Text anlegen" drueckt, will genau
   * diese Zeile -- ein leeres Feld waere die Aufforderung, sie abzutippen.
   * Nur, wenn die Regel in DIESER Sprache gebildet wurde: ein deutscher Satz
   * als Vorbelegung einer englischen Fassung waere eine Uebersetzung, die
   * keine ist.
   */
  const vorbelegung = rule && rule.language === lang && !locale && !text ? rule.body : null;

  return (
    <section className="texts__type">
      <div className="texts__head">
        <h4 className="texts__title">{typeLabel}</h4>
        <QuelleBadge item={item} i18n={i18n} subject={subject} rule={rule} />
        {/* Der Stand gehoert in die Kopfzeile und nicht zwischen die Knoepfe:
            er beschreibt den Text, nicht eine Handlung an ihm. */}
        {locale && (
          <span className={`texts__status texts__status--${locale.status}`}>
            {i18n.tCode("textStatus", locale.status, locale.status)}
          </span>
        )}
      </div>

      {openEigen ? (
        <LocaleForm
          i18n={i18n} item={item} lang={lang} locale={null} text={null}
          subject={subject} supplier={supplier} mayRelease={mayRelease} vorbelegung={null}
          onCancel={() => setEditing(null)}
          onDone={onChanged} onError={onError}
        />
      ) : open ? (
        <LocaleForm
          i18n={i18n} item={item} lang={lang} locale={locale} text={text}
          subject={subject} supplier={supplier} mayRelease={mayRelease} vorbelegung={vorbelegung}
          onCancel={() => setEditing(null)}
          onDone={onChanged} onError={onError}
        />
      ) : locale ? (
        <div className="texts__body">
          <p className="texts__value">{locale.body}</p>

          {/*
            * WAS AN DIESEM TEXT NICHT STIMMT -- im Lesemodus, nicht hinter
            * „Details". Ein Hinweis, den man aufklappen muss, wird nicht
            * gelesen, und alle drei sagen dasselbe: verlass dich auf diese
            * Zeile nicht ungeprueft.
            */}
          {locale.unresolved && locale.unresolved.length > 0 && (
            <p className="texts__warn texts__warn--gap" title={t("texts.unresolvedHint")}>
              {t("texts.unresolved", { list: locale.unresolved.join(", ") })}
            </p>
          )}
          {locale.sourceChangedSince && (
            <p className="texts__warn">{t("texts.sourceChanged")}</p>
          )}
          {locale.basisChanged && (
            <p className="texts__warn texts__warn--basis" title={t("texts.basisChangedHint")}>
              {t("texts.basisChanged")}
            </p>
          )}

          {/* Die Buchhaltung einer Fassung. Zugeklappt im Ruhezustand, aber
              vollstaendig erreichbar -- `details` kostet keine Bibliothek
              und bedient sich mit der Tastatur wie jede Schaltflaeche. */}
          <details className="texts__details">
            <summary className="texts__detailsum">{t("texts.details")}</summary>
            <LocaleMeta locale={locale} i18n={i18n} />
          </details>

          <ReadActions
            item={item} i18n={i18n} lang={lang} typeLabel={typeLabel}
            mayWrite={mayWrite} onEdit={() => setEditing(key)}
            onOwn={() => setEditing(eigenKey)} />
        </div>
      ) : rule ? (
        /*
          * DER REGELGEBILDETE TEXT. Er steht hier, weil der Produktkopf ihn
          * zeigt -- verschwiege ihn diese Ansicht, behauptete derselbe
          * Bildschirm zweierlei. Als Zitat kenntlich: andere Flaeche, eigene
          * Beschriftung, und der Satz darunter sagt, dass nichts gespeichert
          * ist.
          */
        <div className="texts__body">
          <p className="texts__value texts__value--rule">{rule.body}</p>
          {/*
            * DIE ERKLAERUNG STEHT HINTER „DETAILS" -- wie die Buchhaltung
            * jeder anderen Fassung. Drei graue Saetze unter einer einzigen
            * Zeile machten die kleinste Karte zur hoechsten. Dass die Zeile
            * regelgebildet ist, sagt weiterhin der Kopf („regelgeneriert");
            * was das bedeutet, steht einen Klick weiter, vollstaendig.
            */}
          <details className="texts__details">
            <summary className="texts__detailsum">{t("texts.details")}</summary>
          <p className="texts__rulenote">{t("texts.sourceRuleNote")}</p>
          {/* Was als naechstes passiert, haengt an der Sprache: in der Sprache
              der Regel steht die Zeile beim Anlegen schon im Feld, sonst
              beginnt eine fremdsprachige Fassung bei null. Beides muss hier
              stehen -- ein Versprechen, das der Knopf nicht haelt, ist
              schlimmer als keines. */}
          {rule.language === lang ? (
            <p className="texts__rulenote">{t("texts.sourceRuleAdopt")}</p>
          ) : (
            <p className="texts__rulenote">
              {t("texts.sourceRuleLang", { language: i18n.tCode("textLang", rule.language, rule.language) })}
            </p>
          )}
          </details>
          <MissingActions
            item={item} i18n={i18n} lang={lang} text={text} typeLabel={typeLabel}
            mayWrite={mayWrite} onAdd={() => setEditing(key)} onOwn={() => setEditing(eigenKey)} />
        </div>
      ) : (
        <div className="texts__body">
          {/* „Keine Fassung auf Deutsch" -- nicht „kein Text". Der Unterschied
              ist die ganze Auskunft dieser Zeile. */}
          <p className="texts__missing">{t("texts.noLocaleLang", { language: langLabel })}</p>
          {andere.map((l) => (
            <p className="texts__otherlang" key={l}>
              {t("texts.otherLangAvailable", { language: i18n.tCode("textLang", l, l) })}
            </p>
          ))}
          <MissingActions
            item={item} i18n={i18n} lang={lang} text={text} typeLabel={typeLabel}
            mayWrite={mayWrite} onAdd={() => setEditing(key)} onOwn={() => setEditing(eigenKey)} />
        </div>
      )}
    </section>
  );
}

/**
 * WELCHEN TEXT HOMEBASE HIER VERWENDET.
 *
 * Vier Faelle, vier Beschriftungen: eigener Text der SKU, vom Produktmodell
 * geerbt, von der Produktfamilie geerbt, aus einer Regel gebildet. Ohne diese
 * Angabe sehen alle vier gleich aus -- und dann ist nicht zu erkennen, ob
 * eine Aenderung hier wirkt oder eine Ebene hoeher gehoert.
 *
 * Nur an der SKU gibt es ueberhaupt eine Wahl zwischen Ebenen. Am Modell und
 * an der Familie waere die Angabe eine Selbstauskunft der gerade geoeffneten
 * Seite und damit Laerm.
 */
function QuelleBadge({ item, i18n, subject, rule }: {
  item: TextSlot; i18n: I18n; subject: TextSubject;
  rule: { body: string; language: TextLanguageCode } | null;
}): React.JSX.Element | null {
  const t = i18n.t;
  if (subject.kind !== "article") return null;

  if (item.inherited && item.inheritedFrom) {
    const label = t(item.inheritedFrom.level === "model" ? "texts.inheritedFromModel" : "texts.inheritedFrom",
                    { name: item.inheritedFrom.name });
    return <span className="texts__source texts__source--inherited" title={label}>{label}</span>;
  }
  if (item.text) {
    return <span className="texts__source texts__source--own">{t("texts.sourceOwn")}</span>;
  }
  if (rule) {
    return (
      <span className="texts__source texts__source--rule" title={t("texts.sourceRuleNote")}>
        {t("texts.sourceRule")}
      </span>
    );
  }
  return null;
}

/**
 * Der Weg in die Bearbeitung eines vorhandenen Textes.
 *
 * Zwei Faelle, die nicht dasselbe sind: die eigene Fassung aendern -- oder
 * unter einem geerbten Text einen eigenen der SKU beginnen. Der zweite Weg
 * bleibt offen, auch wenn der geerbte Text gut ist; er ist die Ausnahme,
 * deshalb die ruhigere Schaltflaeche.
 */
function ReadActions({ item, i18n, lang, typeLabel, mayWrite, onEdit, onOwn }: {
  item: TextSlot; i18n: I18n; lang: TextLanguageCode; typeLabel: string;
  mayWrite: boolean; onEdit: () => void; onOwn: () => void;
}): React.JSX.Element | null {
  const t = i18n.t;
  if (!mayWrite) return null;
  return (
    <div className="texts__actions">
      {item.editable && (
        <button className="texts__pencil" onClick={onEdit}
          aria-label={t("texts.editAria", { type: typeLabel, language: i18n.tCode("textLang", lang, lang) })}
          title={t("texts.edit")}>✎</button>
      )}
      {!item.editable && item.ownAllowed && (
        <button className="btn btn--small texts__own" onClick={onOwn}>{t("texts.writeOwn")}</button>
      )}
    </div>
  );
}

/**
 * Der Weg zur ersten Fassung -- in dieser Sprache.
 *
 * Auch unter einem GEERBTEN Text, der in dieser Sprache nichts hergibt. Genau
 * dieser Fall ist heute der Regelfall der englischen Akte: am Modell steht ein
 * deutscher Baustein, auf Englisch gar nichts. Ohne diesen Knopf waere die
 * englische Ansicht eine Liste von Luecken ohne jeden Weg heraus -- der
 * geerbte Text bleibt dabei unangetastet, es entsteht ein eigener der SKU.
 */
function MissingActions({ item, i18n, lang, text, typeLabel, mayWrite, onAdd, onOwn }: {
  item: TextSlot; i18n: I18n; lang: TextLanguageCode;
  text: ProductText | null; typeLabel: string; mayWrite: boolean;
  onAdd: () => void; onOwn: () => void;
}): React.JSX.Element | null {
  const t = i18n.t;
  if (!mayWrite) return null;
  if (!item.editable) {
    if (!item.ownAllowed) return null;
    return (
      <div className="texts__actions">
        <button className="btn btn--small texts__own" onClick={onOwn}>{t("texts.writeOwn")}</button>
      </div>
    );
  }
  return (
    <div className="texts__actions">
      <button className="btn btn--small texts__add" onClick={onAdd}
        aria-label={t("texts.addAria", { type: typeLabel, language: i18n.tCode("textLang", lang, lang) })}>
        {text ? t("texts.add") : t("texts.addFirst")}
      </button>
    </div>
  );
}

/**
 * Herkunft, Entstehung, Original- oder Uebersetzungsbezug, Freigabevermerk.
 *
 * Steht im Lesemodus hinter „Details". Diese Angaben beantworten Fragen, die
 * man gelegentlich stellt -- der Stand des Textes und seine Maengel dagegen
 * stehen oben, weil sie einen betreffen, ohne dass man fragt.
 */
function LocaleMeta({ locale, i18n }: { locale: TextLocale; i18n: I18n }): React.JSX.Element {
  const t = i18n.t;
  return (
    <div className="texts__meta">
      {locale.isOriginal
        ? <span className="texts__tag texts__tag--original">{t("texts.original")}</span>
        : locale.translatedFrom && (
            <span className="texts__tag">
              {t("texts.translatedFrom", { language: i18n.tCode("textLang", locale.translatedFrom, locale.translatedFrom) })}
            </span>
          )}
      <span className="texts__tag">{i18n.tCode("textOrigin", locale.origin, locale.origin)}</span>
      {locale.supplier && <span className="texts__tag">{locale.supplier.name}</span>}
      <span className="texts__tag">{i18n.tCode("textMethod", locale.method, locale.method)}</span>
      {/*
        * WER FREIGEGEBEN HAT UND WANN. `updated_by` sagt, wer zuletzt
        * getippt hat -- die redaktionelle Verantwortung traegt aber die
        * Freigabe (Migration 0046).
        */}
      {locale.releasedAt && locale.releasedBy && (
        <span className="texts__tag" title={t("texts.releasedBy",
          { name: locale.releasedBy, date: new Date(locale.releasedAt).toLocaleDateString(i18n.lang) })}>
          {t("texts.releasedBy", { name: locale.releasedBy,
            date: new Date(locale.releasedAt).toLocaleDateString(i18n.lang) })}
        </span>
      )}
    </div>
  );
}

function StatusActions({ locale, i18n, mayRelease, onDone, onError }: {
  locale: TextLocale; i18n: I18n; mayRelease: boolean;
  onDone: () => void; onError: (e: unknown) => void;
}): React.JSX.Element | null {
  const t = i18n.t;
  const [busy, setBusy] = useState(false);
  async function set(status: TextStatus): Promise<void> {
    setBusy(true); onError(null);
    try { await api.updateLocale(locale.id, { status }); onDone(); }
    catch (e) { onError(e); }
    finally { setBusy(false); }
  }
  return (
    <>
      {(locale.status === "entwurf" || locale.status === "pruefung_noetig") && (
        <button className="btn btn--small" disabled={busy} onClick={() => void set("geprueft")}>
          {t("texts.markReviewed")}
        </button>
      )}
      {/*
        * ZUR PRUEFUNG STELLEN -- ohne den Text anzufassen.
        *
        * Nur aus einer Freigabe heraus: `pruefung_noetig` heisst „war
        * freigegeben". Ohne Freigabe waere es ein Entwurf mit anderem
        * Namen, und der Server weist es ab.
        */}
      {locale.status === "freigegeben" && (
        <button className="btn btn--small" disabled={busy} onClick={() => void set("pruefung_noetig")}>
          {t("texts.markReview")}
        </button>
      )}
      {locale.status === "geprueft" && mayRelease && (
        <button className="btn btn--small btn--primary" disabled={busy} onClick={() => void set("freigegeben")}>
          {t("texts.release")}
        </button>
      )}
      {locale.status !== "entwurf" && (
        <button className="btn btn--small" disabled={busy} onClick={() => void set("entwurf")}>
          {t("texts.backToDraft")}
        </button>
      )}
    </>
  );
}

/**
 * Formular fuer genau eine Sprachfassung.
 *
 * Beim Anlegen der ersten Fassung eines Textes ist `isOriginal` fest gesetzt —
 * die erste Fassung ist zwangslaeufig das Original, und das ist keine
 * Entscheidung, die eine Auswahl braucht.
 *
 * Hier -- und nur hier -- stehen alle Handlungen an einer Fassung beieinander:
 * schreiben, Entwurf holen, Herkunft und Entstehung setzen, den Stand aendern,
 * entfernen. Der Lesemodus zeigt sie nicht, weil er sie nicht braucht; die
 * Funktionen sind dadurch nicht weniger geworden, sondern an einem Ort.
 */
function LocaleForm({ i18n, item, lang, locale, text, subject, supplier, mayRelease, vorbelegung, onCancel, onDone, onError }: {
  i18n: I18n; item: TextSlot; lang: TextLanguageCode; locale: TextLocale | null;
  text: ProductText | null; subject: TextSubject; supplier: { id: number; name: string } | null;
  mayRelease: boolean;
  /** Die regelgebildete Zeile, mit der dieses Feld beginnt -- sonst `null`. */
  vorbelegung: string | null;
  onCancel: () => void; onDone: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const isNew = locale === null;
  const isFirst = isNew && text === null;

  const [body, setBody] = useState(locale?.body ?? vorbelegung ?? "");
  const [origin, setOrigin] = useState<TextOrigin>(locale?.origin ?? "intern");
  /* Eine uebernommene Regelzeile ist weder erfasst noch uebersetzt: kein
     Mensch hat sie geschrieben, kein Sprachmodell erzeugt. Sie ist
     reproduzierbar -- dieselben Werte, dieselbe Zeile. */
  const [method, setMethod] = useState<TextMethod>(
    locale?.method ?? (vorbelegung !== null ? "erzeugt_regel" : isFirst ? "erfasst" : "uebersetzt_manuell"));
  const [busy, setBusy] = useState(false);

  /*
   * DER ENTWURF -- und warum er im Formular landet und nicht im Bestand.
   *
   * Der Server bildet ihn aus der Auswahlmatrix der Produktfamilie oder aus
   * dem Baustein des Produktmodells und SPEICHERT ihn nicht. Er erscheint im
   * offenen Eingabefeld: sichtbar, aenderbar, und erst mit dem Speichern ein
   * Text. Ein Knopf, der unmittelbar schreibt, waere ein Weg an der
   * Rechtepruefung und am Protokoll vorbei.
   *
   * Er wird IM Formular geholt und nicht davor. Vorher musste der Aufrufer
   * den Entwurf zwischenlagern und in das gerade erst zu oeffnende Feld
   * einfaedeln; jetzt fuellt der Knopf das Feld, das schon offen vor einem
   * steht -- und er laesst sich zweimal druecken, was vorher nicht ging.
   */
  const [fehlend, setFehlend] = useState<readonly string[] | null>(null);

  async function entwurfHolen(): Promise<void> {
    if (subject.kind !== "article") return;
    onError(null);
    try {
      const e = await api.textDraft(subject.id, item.type, lang);
      setBody(e.text);
      setFehlend(e.fehlend);
      /* Ein regelbasiert gebildeter Entwurf ist weder erfasst noch erzeugt:
         kein Sprachmodell war beteiligt, und ein Mensch hat ihn nicht
         geschrieben. Er ist reproduzierbar -- dieselben Werte, dieselbe
         Zeile. Deshalb `erzeugt_regel`. */
      setMethod("erzeugt_regel");
    } catch (e) { onError(e); }
  }

  // Die Vorlage einer neuen Uebersetzung ist die vorhandene Originalfassung.
  const original = text?.locales.find((l) => l.isOriginal) ?? null;
  const isOriginal = locale?.isOriginal ?? isFirst;
  const tooLong = item.maxLength !== null && body.length > item.maxLength;
  const ready = body.trim() !== "" && !tooLong && !busy;

  /*
   * Eine ungespeicherte Aenderung und ein Statuswechsel vertragen sich nicht:
   * `updateLocale({status})` schickt den Text nicht mit, also fiele das
   * Getippte lautlos unter den Tisch. Deshalb stehen die Standknoepfe still,
   * solange etwas im Feld haengt -- mit dem Satz, warum.
   */
  const dirty = locale !== null && body !== locale.body;

  // Eine Originalfassung kann nicht uebersetzt sein -- die Auswahl bietet
  // deshalb gar nicht erst an, was der Server ablehnen wuerde.
  /* Dieselbe Liste wie im Server -- nicht eine zweite mit gleichem Zweck. */
  const methods = TEXT_METHODS.filter((m) =>
    isOriginal ? (TEXT_ORIGINAL_METHODS as readonly string[]).includes(m) : true);

  async function save(): Promise<void> {
    setBusy(true); onError(null);
    const supplierId = origin === "supplier" ? supplier?.id : undefined;
    try {
      if (locale) {
        await api.updateLocale(locale.id, { body, origin, method, supplierId: supplierId ?? null });
      } else if (text) {
        await api.createLocale(text.id, {
          language: lang, body, isOriginal, origin, method,
          translatedFrom: !isOriginal && original ? original.language : undefined,
          supplierId,
        });
      } else {
        await api.createText({
          type: item.type as TextTypeCode, language: lang, body,
          isOriginal: true, origin, method, supplierId,
          // Genau ein Bezug -- der Server weist alles andere ab.
          ...(subject.kind === "article" ? { articleId: subject.id }
            : subject.kind === "family"    ? { familyId: subject.id }
            :                                { modelId: subject.id }),
        });
      }
      onDone();
    } catch (e) { onError(e); }
    finally { setBusy(false); }
  }

  async function remove(): Promise<void> {
    if (!locale || !window.confirm(t("texts.removeConfirm"))) return;
    setBusy(true); onError(null);
    try { await api.deleteLocale(locale.id); onDone(); }
    catch (e) { onError(e); }
    finally { setBusy(false); }
  }

  return (
    <div className="texts__form">
      {/* Welche Sprache hier entsteht, muss im Formular stehen: der
          Sprachumschalter ist oben rechts und ausser Blickweite, sobald man
          tippt. */}
      <p className="texts__formlang">
        {t("texts.formLanguage", { language: i18n.tCode("textLang", lang, lang) })}
      </p>

      {subject.kind === "article" && (
        <div className="texts__formtools">
          <button className="btn btn--small texts__draft" onClick={() => void entwurfHolen()}
            title={t("texts.draftTitle")}>{t("texts.draft")}</button>
        </div>
      )}

      {/*
        * WAS DEM ENTWURF GEFEHLT HAT.
        *
        * Steht UEBER dem Feld, nicht darunter: wer die Zeile liest, muss
        * vorher wissen, dass ein Abschnitt fehlt. Ein Generator, der eine
        * Luecke still ueberspringt, liefert einen Text, der vollstaendig
        * aussieht und es nicht ist.
        */}
      {fehlend !== null && fehlend.length > 0 && (
        <p className="texts__draftgap" role="status">
          {t("texts.draftMissing", { list: fehlend.join(", ") })}
        </p>
      )}
      {fehlend !== null && fehlend.length === 0 && (
        <p className="texts__drafthint" role="status">{t("texts.draftReady")}</p>
      )}
      <textarea className="texts__input" value={body} rows={5} autoFocus
        onChange={(e) => setBody(e.target.value)} />

      <div className="texts__formmeta">
        {item.maxLength !== null && (
          <span className={tooLong ? "texts__count texts__count--over" : "texts__count"}>
            {t("texts.charsUsed", { used: body.length, max: item.maxLength })}
          </span>
        )}
        {isOriginal && <span className="texts__tag texts__tag--original">{t("texts.isOriginal")}</span>}
        {!isOriginal && original && (
          <span className="texts__tag">
            {t("texts.translatedFrom", { language: i18n.tCode("textLang", original.language, original.language) })}
          </span>
        )}
      </div>

      <div className="texts__fields">
        <label className="texts__field">
          <span>{t("texts.origin")}</span>
          <select value={origin} onChange={(e) => setOrigin(e.target.value as TextOrigin)}>
            {TEXT_ORIGINS.map((o) => (
              // Supplier-Herkunft nur, wenn der Artikel einen Supplier hat --
              // sonst wuerde der Server sie mit gutem Grund ablehnen.
              <option key={o} value={o} disabled={o === "supplier" && !supplier}>
                {i18n.tCode("textOrigin", o, o)}
              </option>
            ))}
          </select>
        </label>
        <label className="texts__field">
          <span>{t("texts.method")}</span>
          <select value={method} onChange={(e) => setMethod(e.target.value as TextMethod)}>
            {methods.map((m) => (
              <option key={m} value={m}>{i18n.tCode("textMethod", m, m)}</option>
            ))}
          </select>
        </label>
        {origin === "supplier" && supplier && (
          <div className="texts__field">
            <span>{t("texts.supplier")}</span>
            <div className="texts__supplier">{supplier.name}</div>
          </div>
        )}
      </div>

      {locale && locale.status !== "entwurf" && (
        <p className="texts__note">{t("texts.editedResetsStatus")}</p>
      )}

      <div className="texts__formactions">
        <button className="btn btn--primary btn--small" disabled={!ready} onClick={() => void save()}>
          {busy ? t("texts.saving") : t("texts.save")}
        </button>
        <button className="btn btn--small" onClick={onCancel}>{t("common.cancel")}</button>
        {locale && (
          <button className="btn btn--small texts__danger" disabled={busy} onClick={() => void remove()}>
            {t("texts.remove")}
          </button>
        )}
      </div>

      {/* Der Stand der Fassung -- pruefen, freigeben, zuruecksetzen. Abgesetzt
          von Speichern und Entfernen, weil er den Text nicht aendert. */}
      {locale && (
        dirty ? (
          <p className="texts__note">{t("texts.statusAfterSave")}</p>
        ) : (
          <div className="texts__statusrow">
            <StatusActions locale={locale} i18n={i18n} mayRelease={mayRelease}
              onDone={onDone} onError={onError} />
          </div>
        )
      )}
    </div>
  );
}
